import { Inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  find,
  first,
  forEach,
  get,
  includes,
  isEmpty,
  map,
  pick,
  round,
  split,
} from 'lodash';
import {
  BehaviorSubject,
  filter,
  filter as rxFilter,
  Observable,
  takeUntil,
  tap,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  DynamicFieldAction,
  DynamicFormExpressions,
  DynamicFormExtension,
  DynamicFormHooks,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import {
  ApplicationConfigStep,
  ApplicationStep,
  Client,
  Contact,
  Partners,
} from '@lockton-apps/shared/interfaces';

import { AddressPipe, FeedbackService } from '@lockton-apps/shared/ui/lui';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';

interface PartnerData {
  allProductIds: string[];
  client: Client;
  portalId: Partners;
  configSteps: ApplicationConfigStep[];
  proposalSteps: ApplicationStep[];
  selectedProducts: number[];
  contact: Contact;
}

@Injectable()
export class PncDynamicFormsExtensionsService extends DynamicFormsExtensionsService {
  crimeEmployeesLocationList$ = new BehaviorSubject<
    { label: string; value: string }[]
  >([]);
  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<PartnerData>,
    private readonly addressPipe: AddressPipe,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService
    );
  }

  override readonly extensions: DynamicFormExtension[] = [
    {
      id: 'allLocationsDisplayWith',
      prop: 'displayWith',
      value: (value: boolean) => (value ? $localize`Yes` : $localize`No`),
    },
    {
      id: 'derivedLocationDisplayWith',
      prop: 'displayWith',
      value: (value: any, parentValue: any) => {
        if (parentValue?.allLocations) {
          return $localize`all locations`;
        }

        return value ? this.addressPipe.transform(value.address) : '-';
      },
    },
    {
      id: 'locationOptions',
      prop: 'options',
      value: [],
      mappedValue: (field, model) => {
        const locationsModel = get(
          model,
          'aboutYourCompany.locations.list',
          []
        );

        return map(locationsModel, (location) => ({
          label: this.addressPipe.transform(location.address),
          value: this.getLocationValueFromOrigin(location),
        }));
      },
    },
  ];

  override hooks: DynamicFormHooks[] = [
    {
      id: 'distributionCalcTableRowOptions',
      hookConfig: {
        onInit: (field) => {
          return field.options?.fieldChanges?.pipe(
            rxFilter(
              (e) =>
                e.type === 'valueChanges' &&
                e.field.key ===
                  'businessInterruption.valuesLimitsIndemnity.valueLimitsLossSelect'
            ),
            tap((e) => {
              const map = new Map<string, string>([
                ['estimatedGrossProfit', 'EAGP'],
                ['estimatedAnnualGrossRevenue', 'EAGR'],
                ['costsOfWorking', 'ICW'],
              ]);

              const tableRows = field?.props?.['tableRows'];

              forEach(
                tableRows,
                (row) => map.get(row.id) && (row.hidden = true)
              );

              const limitLossSelectValue = e.value;

              if (limitLossSelectValue) {
                const tableRowId = first(
                  find(
                    [...map.entries()],
                    ([, value]) => value === limitLossSelectValue
                  )
                );
                const tableRow = find(tableRows, { id: tableRowId });
                tableRow.hidden = false;
              }
              field?.options?.detectChanges?.(field);
            })
          );
        },
      },
    },
    {
      id: 'businessInterruptionSumInsured',
      hookConfig: {
        onInit: (field) =>
          field.options?.fieldChanges?.pipe(
            rxFilter((e) => e.type === 'valueChanges'),
            tap(() => {
              const value = round(
                (field.model?.annualSumInsured ?? 0) *
                  (field.model?.indemnityPeriod / 12 || 0),
                2
              );

              field.formControl?.setValue(value, { emitEvent: false });
              field?.options?.detectChanges?.(field);
            })
          ),
      },
    },
    {
      id: 'fetchEmployeeListCountryOptions',
      hookConfig: {
        onInit: (field) => {
          if (field.options?.formState) {
            const locations = field.model?.crime?.employees?.locationList ?? [];
            this.crimeEmployeesLocationList$.next(
              this.mapLocationsToOptions(locations)
            );
          }

          const formControl = field.form?.get(
            'crime.employees.locationList'
          ) as FormControl;
          return formControl.valueChanges.pipe(
            tap((values) => {
              this.crimeEmployeesLocationList$.next(
                this.mapLocationsToOptions(values)
              );
            })
          );
        },
      },
    },
    {
      id: 'locationId',
      hookConfig: {
        onInit: (field) => {
          if (!field.formControl || !!field.model.id) {
            return;
          }

          field.formControl.setValue(uuidv4(), { emitEvent: false });
        },
      },
    },
    {
      id: 'locationOrigin',
      hookConfig: {
        afterViewInit: (field) => {
          if (!field.formControl) {
            return;
          }

          field.options?.formState
            .externalAction$()
            .pipe(
              filter((action: DynamicFieldAction) =>
                includes(
                  [DynamicFieldAction.Create, DynamicFieldAction.Delete],
                  action
                )
              ),
              takeUntil(this.destroyNotifier)
            )
            .subscribe(() => {
              const originModel = field.formControl?.parent?.parent?.value;

              forEach(field.props?.['dependantFields'], (fieldId: string) => {
                const [dependantFieldKey, dependantInternalFieldKey] = split(
                  fieldId,
                  ':'
                );
                const dependantFields = get(
                  field.options?.formState.model(),
                  dependantFieldKey
                );

                forEach(dependantFields, (value): boolean | void => {
                  if (value[dependantInternalFieldKey]) {
                    const updatedLocationOnOriginModel = find(originModel, {
                      id: value[dependantInternalFieldKey].id,
                    });

                    if (updatedLocationOnOriginModel) {
                      value[dependantInternalFieldKey] =
                        this.getLocationValueFromOrigin(
                          updatedLocationOnOriginModel
                        );
                      return true;
                    }

                    delete value[dependantInternalFieldKey];
                  }
                });
              });
            });
        },
      },
    },
  ];

  override expressions: Record<string, DynamicFormExpressions> = {
    employeeListCountryOptions: {
      'props.options': () => this.crimeEmployeesLocationList$,
    },
    contactListDefaultContact: {
      defaultValue: (field) => {
        let value = field.defaultValue;
        if (
          (!field.defaultValue || isEmpty(field.defaultValue)) &&
          field.options?.formState?.partnerData?.contact
        ) {
          value = [field.options?.formState?.partnerData?.contact];
          field.formControl?.setValue(value);
        }
        return value;
      },
    },
    clientDefaultAddress: {
      defaultValue: (field) => {
        let value = field.defaultValue;
        if (
          (!field.defaultValue || isEmpty(field.defaultValue)) &&
          field.options?.formState?.partnerData?.contact
        ) {
          value = field.options?.formState?.partnerData?.clientAddress;
          field.formControl?.setValue(value);
        }
        return value;
      },
    },
    clientDefaultName: {
      defaultValue: (field) => {
        let value = field.defaultValue;
        if (
          (!field.defaultValue || isEmpty(field.defaultValue)) &&
          field.options?.formState?.partnerData?.contact
        ) {
          value = field.options?.formState?.partnerData?.clientName;
          field.formControl?.setValue(value);
        }
        return value;
      },
    },
  };

  private mapLocationsToOptions(values: any) {
    return map(values, (value) => ({
      label: value.country,
      value: value.country,
    }));
  }

  private getLocationValueFromOrigin(model: any) {
    return pick(model, ['id', 'address']);
  }
}
